import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  Construction,
  Mail,
  Schedule,
  Storage,
  CloudUpload,
  Psychology,
  Description,
  HealthAndSafety,
  FactCheck,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import { DevZugangVerwaltung } from './DevZugangVerwaltung';

/**
 * Versteckter Admin-Hub für Tools, die nicht im normalen Menü stehen sollen
 * (Datenbank-/Kataloge-Pflege, System-Mail-Templates, …). Aufruf:
 * /admin/dev — kein Eintrag im AppShell, nur per direkter URL.
 *
 * Nur für Benutzer mit Entwickler-Zugang (`User.isDev`, Recht `dev`) — die
 * Seite und ihre Unterseiten ebenso wie die Endpunkte dahinter. Vergeben wird
 * der Zugang unten auf dieser Seite, nicht in der Benutzerverwaltung.
 */

interface DevEntry {
  path: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ENTRIES: DevEntry[] = [
  {
    path: '/admin/sysreg',
    title: 'System Registry',
    description:
      'Bearbeiten der system-internen Settings (Setting-Tabelle). Vorsicht: Änderungen können das System irreversibel beschädigen.',
    icon: <Storage fontSize='large' />,
  },
  {
    path: '/admin/fints-institutes',
    title: 'FinTS-Listen Management',
    description:
      'Bank-Katalog für HBCI/FinTS-Anbindung. Upload der ZKA-Excel-Liste (Bankleitzahlen + FinTS-URLs), optional als Migration für alle Kunden.',
    icon: <AccountBalance fontSize='large' />,
  },
  {
    path: '/admin/system_mail/list',
    title: 'System-Mail-Vorlagen',
    description:
      'Verwaltung der System-Mail-Templates (Benachrichtigungen, Reminder, automatisierte E-Mails).',
    icon: <Mail fontSize='large' />,
  },
  {
    path: '/admin/dev/processmanager',
    title: 'ProcessManager (inkl. Automatisierungen)',
    description:
      'Roh-Zugriff auf die ProcessManager-Tabelle: Trigger einzelner Einträge, Edit von data/State/Tries, Bulk-Export/Import, Sperrzeiten der PM-Typen. Für Support & Debug — Änderungen sind nicht rückgängig zu machen.',
    icon: <Schedule fontSize='large' />,
  },
  {
    path: '/admin/dev/seed',
    title: 'Stammdaten-Seed / Katalog-Import',
    description:
      'Katalog-JSONs (Amtsgerichte, Grundbuch-Gemarkungen je Bundesland) hochladen und daraus eine idempotente Seed-Migration erzeugen. Ersetzt das manuelle DB-/Skript-Seeding — die Migration wird in Dev geprüft und rollt bei allen Instanzen aus.',
    icon: <CloudUpload fontSize='large' />,
  },
  {
    path: '/admin/dev/ki',
    title: 'KI-Anbindung',
    description:
      'Anbieter und Modell wählen (Claude, ChatGPT, künftig gpt-oss-120b über IBM watsonx.ai), Zugangsdaten hinterlegen und die KI-Funktionen als Ganzes ein- oder ausschalten. Enthält den Modellvergleich: dieselbe Frage an mehrere Anbieter. Die Inhalte gehen unverändert an den Anbieter — es findet keine Anonymisierung statt.',
    icon: <Psychology fontSize='large' />,
  },
  {
    path: '/admin/dev/word-plugin',
    title: 'Word-Plugin freischalten',
    description:
      'Hauptschalter und Benutzerliste für das Word-Plugin: Urkundenerstellung im Vorgang, Dokumentvorlagen und Word-Add-in sehen nur die angehakten Personen. Leer heißt niemand.',
    icon: <Description fontSize='large' />,
  },
  {
    path: '/admin/dev/healthcheck',
    title: 'Health Check freischalten',
    description:
      'Hauptschalter und Benutzerliste für den Health Check bei Vorgängen: Prüfung beim Speichern, Knopf im Vorgang und Vorgang-Check in den Systemeinstellungen haben nur die angehakten Personen. Leer heißt niemand.',
    icon: <HealthAndSafety fontSize='large' />,
  },
  {
    path: '/admin/dev/gwgpruefung',
    title: 'GwG-Prüfung freischalten',
    description:
      'Benutzerliste für die GwG-Prüfung nach dem BNotK-Fragenkatalog: nur die angehakten Personen sehen sie im Vorgang, alle anderen behalten die bisherige Ansicht. Leer heißt niemand.',
    icon: <FactCheck fontSize='large' />,
  },
];

export const DevHubOverview: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
      <Construction />
      <Typography variant='h5'>Admin-Hub (intern)</Typography>
    </Stack>

    <Alert severity='warning' sx={{ mb: 3 }}>
      Versteckte Admin-Werkzeuge. Diese Seiten sind nicht im Standardmenü
      verlinkt, weil sie bei falscher Benutzung das System beschädigen können.
      Nur für Lextorbyte-Entwickler / Hersteller-Admin gedacht.
    </Alert>

    <Box sx={{ mb: 3 }}>
      <DevZugangVerwaltung />
    </Box>

    <Stack spacing={2}>
      {ENTRIES.map(entry => (
        <Card key={entry.path} variant='outlined'>
          <CardActionArea component={RouterLink} to={entry.path}>
            <CardContent>
              <Stack direction='row' spacing={2} alignItems='flex-start'>
                <Box sx={{ color: 'primary.main', mt: 0.5 }}>{entry.icon}</Box>
                <Box>
                  <Typography variant='subtitle1' fontWeight={600}>
                    {entry.title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {entry.description}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.disabled'
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {entry.path}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>
  </Box>
);
